import type { TranslationFunction } from "../../../utils/utils";
import {
  referenceKey,
  type HistoryCollection,
  type RecordLabels,
} from "../../../core/history/entities/HistoryVersion";

/// Traduction des noms de champs et mise en forme des valeurs pour l'historique.
///
/// Les snapshots portent les noms de colonnes de la base (`categoryId`, `contentText`…) : les
/// afficher tels quels exposerait le schéma à l'utilisateur. Les libellés existants des formulaires
/// sont réutilisés (`<entité>.field.<nom>`), avec repli sur un libellé technique partagé, puis sur
/// le nom brut.

/// Préfixe i18n des libellés de champs, par collection.
const ENTITY_KEYS: Record<HistoryCollection, string> = {
  topics: "topic",
  topicParts: "topicPart",
  topicPartQuestions: "topicPartQuestion",
  testimonies: "testimony",
  trainingVersions: "trainingVersion",
};

/// Colonnes dont le libellé de formulaire porte un autre nom que la colonne elle-même.
const FIELD_ALIASES: Partial<Record<HistoryCollection, Record<string, string>>> = {
  topics: {
    categoryId: "category",
    relatedTopicIds: "relatedTopics",
  },
  topicParts: {
    timing: "duration",
  },
};

/// Champs de texte riche (HTML produit par Quill).
const RICH_TEXT_FIELDS = [
  "introduction",
  "synthesis",
  "conclusion",
  "contentText",
  "additionalText",
  "text",
  "description",
];

/// Libellé lisible d'un champ.
export function fieldLabel(
  collection: HistoryCollection,
  field: string,
  t: TranslationFunction,
): string {
  const alias = FIELD_ALIASES[collection]?.[field] ?? field;
  const entityKey = `${ENTITY_KEYS[collection]}.field.${alias}`;

  // i18next renvoie la clé elle-même quand la traduction est absente.
  const entityLabel = t(entityKey);
  if (entityLabel !== entityKey) return entityLabel;

  const sharedKey = `history.field.${field}`;
  const sharedLabel = t(sharedKey);
  if (sharedLabel !== sharedKey) return sharedLabel;

  return field;
}

/// Texte brut d'un fragment HTML, pour comparer le contenu et non le balisage.
function htmlToText(html: string): string {
  try {
    const parsed = new DOMParser().parseFromString(html, "text/html");
    return (parsed.body.textContent ?? "").replace(/\n{3,}/g, "\n\n").trim();
  } catch {
    return html;
  }
}

/// Un enregistrement désigné par la valeur d'un champ relation.
export interface ValueReference {
  id: string;
  /// Libellé résolu par le backend, vide si l'enregistrement est introuvable.
  label: string;
  isDeleted: boolean;
}

export interface FormattedValue {
  /// Texte affichable, déjà dégagé du balisage pour les champs de texte riche.
  text: string;
  /// Vrai quand la valeur est absente ou vide.
  isEmpty: boolean;
  /// Renseigné quand la valeur est un (ou plusieurs) id d'enregistrement : à afficher comme des
  /// références nommées plutôt que comme du texte.
  references?: ValueReference[];
}

/// Références portées par la valeur de [field], ou `null` si la valeur n'en est pas une.
///
/// Le backend produit une entrée de [labels] pour chaque id trouvé dans un champ relation, même
/// irrésoluble : la présence de la clé suffit donc à reconnaître une référence, sans dupliquer ici
/// la liste des champs relation de chaque collection.
function toReferences(
  field: string,
  value: unknown,
  labels: RecordLabels,
): ValueReference[] | null {
  const items = Array.isArray(value) ? value : [value];

  if (!items.length) return null;
  if (
    !items.every(
      (item) => typeof item === "string" && !!labels[referenceKey(field, item)],
    )
  ) {
    return null;
  }

  return (items as string[]).map((id) => ({
    id,
    label: labels[referenceKey(field, id)].label,
    isDeleted: labels[referenceKey(field, id)].isDeleted,
  }));
}

/// Rend une valeur de champ lisible, tous types confondus.
///
/// [labels] identifie les enregistrements désignés par un id, pour les nommer au lieu de laisser
/// une chaîne de 15 caractères illisible.
export function formatFieldValue(
  field: string,
  value: unknown,
  emptyLabel: string,
  labels: RecordLabels = {},
): FormattedValue {
  if (value === null || value === undefined || value === "") {
    return { text: emptyLabel, isEmpty: true };
  }

  // Avant le traitement des tableaux : une relation multiple en est un.
  const references = toReferences(field, value, labels);
  if (references) {
    return {
      text: references.map((reference) => reference.label || reference.id).join(" · "),
      isEmpty: false,
      references,
    };
  }

  if (typeof value === "boolean") {
    return { text: value ? "✓" : "✗", isEmpty: false };
  }

  if (Array.isArray(value)) {
    if (!value.length) return { text: emptyLabel, isEmpty: true };
    return { text: value.map((item) => String(item)).join(" · "), isEmpty: false };
  }

  if (typeof value === "object") {
    return { text: JSON.stringify(value, null, 2), isEmpty: false };
  }

  const raw = String(value);

  if (RICH_TEXT_FIELDS.includes(field) && /<[a-z][\s\S]*>/i.test(raw)) {
    const text = htmlToText(raw);
    return text
      ? { text: text, isEmpty: false }
      : { text: emptyLabel, isEmpty: true };
  }

  return { text: raw, isEmpty: false };
}

/// Vrai si seul le balisage a changé : les deux textes affichés seraient identiques.
///
/// Sans ce test, une modification de mise en forme ferait apparaître deux colonnes visuellement
/// identiques, ce qui donne l'impression d'un bug.
export function isMarkupOnlyChange(
  field: string,
  before: unknown,
  after: unknown,
): boolean {
  if (typeof before !== "string" || typeof after !== "string") return false;
  if (!RICH_TEXT_FIELDS.includes(field)) return false;
  if (before === after) return false;

  return htmlToText(before) === htmlToText(after);
}
